import React from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, MenuItem } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { PhysicianType } from "../../../../services/ConfigurationService/types/physicianType";
import { useUpdatePhysicianMutation, useGetSpecialtiesQuery } from '../../../../services/ConfigurationService';
import { useLocation } from 'react-router-dom';

interface AddPhysicianDialogProps {
  defaultCenterId: string;
  defaultCompany: string;
  onAdd?: () => void;
}

export function AddPhysicianDialog({ defaultCenterId, defaultCompany, onAdd }: AddPhysicianDialogProps) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const companyFromUrl = query.get('company') || defaultCompany;
  const { data: specialties, isLoading: specialtiesLoading } = useGetSpecialtiesQuery();
  const [open, setOpen] = React.useState(false);
  const [updatePhysician] = useUpdatePhysicianMutation();
  const [form, setForm] = React.useState({
    id: '', // Add id to form state
    code: "",
    centerId: defaultCenterId,
    firstName: "",
    middleName: "", // keep for type compatibility
    lastName: "",
    specialtyName: "Unknown",
    specialtyValue: "UNK",
    npi: "",
    phone: "",
    company: companyFromUrl,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value, company: companyFromUrl }));
  };

  const handleAdd = async () => {
    // Set id as centerId-code before saving
    const newPhysician = { ...form, id: `${form.centerId}-${form.code}`, company: companyFromUrl };
    await updatePhysician(newPhysician);
    if (onAdd) onAdd();
    setOpen(false);
    setForm({
      id: '',
      code: "",
      centerId: defaultCenterId,
      firstName: "",
      middleName: "",
      lastName: "",
      specialtyName: "Unknown",
      specialtyValue: "UNK",
      npi: "",
      phone: "",
      company: companyFromUrl,
    });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Add Physician
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Physician</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Code" name="code" value={form.code} onChange={handleChange} fullWidth required />
            <TextField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} fullWidth required />
            <TextField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} fullWidth required />
            <TextField
              label="Specialty"
              name="specialtyValue"
              value={form.specialtyValue}
              onChange={handleChange}
              fullWidth
              required
              select
              disabled={specialtiesLoading}
            >
              {(specialties || []).map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.name}</MenuItem>
              ))}
            </TextField>
            <TextField label="NPI" name="npi" value={form.npi} onChange={handleChange} fullWidth />
            <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
